/**
 * LyricsStatusBadge
 *
 * Inline badge shown below the MP3 upload strip in Sing4YouTalentPage.
 * Five visual states:
 *
 *   searching  — spinner while LRCLIB is queried
 *   found      — "Lyrics synced ✓  Hello — Adele"          auto-injected
 *   plain      — "Lyrics loaded (no timing)  Hello — Adele" auto-injected
 *   confirm    — "Did you mean: Hello — Adele? [✓ Yes] [✗ No]"
 *   not_found  — "No lyrics found — Upload .lrc manually"
 *   error      — "Lyrics fetch failed — Upload .lrc manually"
 *
 * The "confirm" state is shown when the match confidence is low — e.g.
 * the talent uploads "hello.mp3" (single common word) and LRCLIB returns
 * "Hello — Adele". The talent taps Yes to accept or No to dismiss.
 */

import React from "react";
import type { LyricsStatus } from "./Lyrics/useLyricsautofetch";

interface Props {
  status:         LyricsStatus;
  title?:         string;
  artist?:        string;
  onConfirm?:     () => void;   // talent accepts a low-confidence match
  onReject?:      () => void;   // talent rejects — falls back to manual
  onManualUpload?: () => void;  // opens the Studio for manual .lrc upload
}

const SPIN = `@keyframes s4y-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`;

export default function LyricsStatusBadge({
  status, title, artist, onConfirm, onReject, onManualUpload,
}: Props) {
  if (status === "idle") return null;

  const matchLabel = title
    ? `${title}${artist ? ` — ${artist}` : ""}`
    : null;

  /* ── Searching ─────────────────────────────────────────────────────── */
  if (status === "searching") {
    return (
      <>
        <style>{SPIN}</style>
        <div style={wrap("rgba(99,102,241,0.1)", "rgba(99,102,241,0.3)")}>
          <div style={{
            width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
            border: "2px solid rgba(99,102,241,0.3)", borderTopColor: "#818cf8",
            animation: "s4y-spin 0.7s linear infinite",
          }} />
          <span style={{ color: "#818cf8", fontSize: "0.75rem", fontWeight: 600 }}>
            Searching for lyrics…
          </span>
        </div>
      </>
    );
  }

  /* ── Found (high confidence, synced) ───────────────────────────────── */
  if (status === "found") {
    return (
      <div style={wrap("rgba(0,168,107,0.1)", "rgba(0,168,107,0.3)")}>
        <span style={{ fontSize: "0.9rem" }}>🎵</span>
        <span style={{ color: "#00c896", fontSize: "0.75rem", fontWeight: 700 }}>
          Lyrics synced ✓
        </span>
        {matchLabel && (
          <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}>
            {matchLabel}
          </span>
        )}
      </div>
    );
  }

  /* ── Plain (high confidence, no real timestamps) ───────────────────── */
  if (status === "plain") {
    return (
      <div style={wrap("rgba(245,166,35,0.1)", "rgba(245,166,35,0.3)")}>
        <span style={{ fontSize: "0.9rem" }}>📝</span>
        <span style={{ color: "#F5A623", fontSize: "0.75rem", fontWeight: 700 }}>
          Lyrics loaded (no timing)
        </span>
        {matchLabel && (
          <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}>
            {matchLabel}
          </span>
        )}
      </div>
    );
  }

  /* ── Confirm (low confidence — ask talent) ─────────────────────────── */
  if (status === "confirm") {
    return (
      <div style={{
        ...wrap("rgba(99,102,241,0.08)", "rgba(99,102,241,0.25)"),
        flexWrap: "wrap", gap: 10,
      }}>
        <span style={{ fontSize: "0.9rem" }}>🎵</span>
        <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.75rem" }}>
          Did you mean:
        </span>
        <span style={{ color: "#fff", fontSize: "0.75rem", fontWeight: 700 }}>
          {matchLabel ?? "Unknown song"}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={onConfirm}
            style={btnStyle("#00c896", "rgba(0,168,107,0.15)", "rgba(0,168,107,0.4)")}
          >
            ✓ Yes, use this
          </button>
          <button
            onClick={onReject}
            style={btnStyle("rgba(255,255,255,0.5)", "rgba(255,255,255,0.05)", "rgba(255,255,255,0.15)")}
          >
            ✗ No
          </button>
        </div>
      </div>
    );
  }

  /* ── Not found / Error ─────────────────────────────────────────────── */
  if (status === "not_found" || status === "error") {
    return (
      <div style={wrap("rgba(255,255,255,0.03)", "rgba(255,255,255,0.1)")}>
        <span style={{ fontSize: "0.9rem" }}>🔍</span>
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem" }}>
          {status === "not_found" ? "No lyrics found automatically" : "Lyrics fetch failed"}
        </span>
        {onManualUpload && (
          <button
            onClick={onManualUpload}
            style={btnStyle("rgba(255,255,255,0.6)", "rgba(255,255,255,0.05)", "rgba(255,255,255,0.15)")}
          >
            Upload .lrc manually
          </button>
        )}
      </div>
    );
  }

  return null;
}

/* ── Style helpers ──────────────────────────────────────────────────────── */

function wrap(bg: string, border: string): React.CSSProperties {
  return {
    display: "flex", alignItems: "center", gap: 8, flexShrink: 0,
    padding: "7px 13px",
    background: bg, border: `1px solid ${border}`,
    borderRadius: 8, whiteSpace: "nowrap",
  };
}

function btnStyle(color: string, bg: string, border: string): React.CSSProperties {
  return {
    background: bg, border: `1px solid ${border}`,
    borderRadius: 6, color, fontSize: "0.72rem",
    fontWeight: 700, padding: "3px 10px", cursor: "pointer",
    whiteSpace: "nowrap",
  };
}